<!--
  ReservationOverrideModal.vue

  2025-06-04: Improved UI design, disabled price field, added calculation note
  2025-06-04: Replaced HTML5 date inputs with v-date-picker components to match application standards
  2025-06-04: Added check-in and check-out date fields to support date changes in override modal
  2024-03-10: Initial implementation
-->
<template>
  <v-card min-width="800" max-width="900">
    <v-toolbar elevation="0" dense hide-details color="primary" dark>
      <v-toolbar-title class="text-h6 font-weight-medium">
        <v-icon left>mdi-pencil</v-icon>
        Reservation Override
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text class="pa-6">
      <v-alert
        v-if="unsynced"
        type="warning"
        text
        dense
        class="mb-4"
        border="left"
      >
        <v-icon slot="prepend">mdi-alert</v-icon>
        This reservation is currently overridden and not synced with PMS
      </v-alert>

      <v-form ref="form">
        <!-- Dates Section -->
        <div class="mb-6">
          <div class="text-subtitle-1 font-weight-medium mb-3 primary--text">
            <v-icon left color="primary">mdi-calendar</v-icon>
            Reservation Dates
          </div>
          <v-row>
            <v-col cols="6">
              <v-menu
                v-model="checkInMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.check_in"
                    outlined
                    dense
                    hide-details
                    label="Check-in Date"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    :rules="[required]"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="form.check_in"
                  @input="checkInMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-menu
                v-model="checkOutMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.check_out"
                    outlined
                    dense
                    hide-details
                    label="Check-out Date"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    :rules="[required, validCheckOutDate]"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="form.check_out"
                  :min="form.check_in"
                  @input="checkOutMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
        </div>

        <!-- Pricing Section -->
        <div class="mb-6">
          <div class="text-subtitle-1 font-weight-medium mb-3 primary--text">
            <v-icon left color="primary">mdi-currency-usd</v-icon>
            Pricing Details
          </div>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.fare_accommodation"
                outlined
                :rules="[required]"
                dense
                hide-details="auto"
                label="Fare Accommodation"
                prefix="$"
                type="number"
                step="0.01"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.cleaning_fee"
                outlined
                :rules="[required]"
                dense
                hide-details="auto"
                label="Cleaning Fee"
                prefix="$"
                type="number"
                step="0.01"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.processing_fee"
                outlined
                :rules="[required]"
                dense
                hide-details="auto"
                label="Processing Fee"
                prefix="$"
                type="number"
                step="0.01"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.price"
                outlined
                dense
                hide-details="auto"
                label="Total Price"
                prefix="$"
                disabled
                class="grey lighten-4"
              />
              <div class="text-caption grey--text mt-1">
                <v-icon small>mdi-information</v-icon>
                Calculated automatically: Fare Accommodation + Cleaning Fee +
                Taxes
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Channel & Commission Section -->
        <div class="mb-6">
          <div class="text-subtitle-1 font-weight-medium mb-3 primary--text">
            <v-icon left color="primary">mdi-percent</v-icon>
            Fees & Commissions
          </div>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.host_channel_fee"
                outlined
                :rules="[required]"
                dense
                hide-details="auto"
                label="Host Channel Fee"
                prefix="$"
                type="number"
                step="0.01"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.actual_cleaning_paid"
                outlined
                :rules="[required]"
                dense
                hide-details="auto"
                label="Actual Cleaning Paid"
                prefix="$"
                type="number"
                step="0.01"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.management_commission"
                outlined
                :rules="[required]"
                dense
                hide-details="auto"
                label="Management Commission"
                prefix="$"
                type="number"
                step="0.01"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.net_income"
                outlined
                :rules="[required]"
                dense
                hide-details="auto"
                label="Net Income"
                prefix="$"
                type="number"
                step="0.01"
              />
            </v-col>
          </v-row>
        </div>

        <!-- Owner Profit Section -->
        <div class="mb-4">
          <div class="text-subtitle-1 font-weight-medium mb-3 primary--text">
            <v-icon left color="primary">mdi-account-cash</v-icon>
            Owner Information
          </div>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.client_profit"
                outlined
                :rules="[required]"
                dense
                hide-details="auto"
                label="Owner Profit"
                prefix="$"
                type="number"
                step="0.01"
              />
            </v-col>
          </v-row>
        </div>

        <v-alert
          v-if="showAlert && !unsynced"
          type="warning"
          text
          dense
          border="left"
          class="mt-4"
        >
          <v-icon slot="prepend">mdi-alert-circle</v-icon>
          <strong>Important:</strong> Changing these values will unsync this
          reservation from the PMS system.
        </v-alert>
      </v-form>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions class="pa-4">
      <v-spacer></v-spacer>
      <v-btn color="grey" text class="mr-2" @click="close">
        <v-icon left>mdi-close</v-icon>
        Cancel
      </v-btn>
      <v-btn color="primary" :loading="saving" @click="save">
        <v-icon left>mdi-content-save</v-icon>
        Save Override
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import formRulesMixin from 'components/mixins/form-rules-mixin'

export default {
  name: 'ReservationOverrideModal',
  props: {
    reservation: Object,
    onSuccess: Function,
  },
  mixins: [formRulesMixin],
  data() {
    return {
      showAlert: false,
      unsynced: this.reservation.pms_unsync,
      checkInMenu: false,
      checkOutMenu: false,
      saving: false,
      form: {
        check_in: this.formatDate(this.reservation.check_in),
        check_out: this.formatDate(this.reservation.check_out),
        fare_accommodation: this.formatNumber(
          this.reservation.fare_accommodation
        ),
        cleaning_fee: this.formatNumber(this.reservation.cleaning_fee),
        processing_fee: this.formatNumber(this.reservation.processing_fee),
        host_channel_fee: this.formatNumber(this.reservation.host_channel_fee),
        client_profit: this.formatNumber(this.reservation.client_profit),
        net_income: this.formatNumber(this.reservation.net_income),
        actual_cleaning_paid: this.formatNumber(
          this.reservation.actual_cleaning_paid
        ),
        price: this.formatNumber(this.reservation.price),
        management_commission: this.formatNumber(
          this.reservation.management_commission
        ),
      },
    }
  },
  watch: {
    form: {
      handler() {
        this.showAlert = true
      },
      deep: true,
    },
  },
  methods: {
    formatNumber(value) {
      return parseFloat(value || 0).toFixed(2)
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toISOString().split('T')[0]
    },
    validCheckOutDate(value) {
      if (!value || !this.form.check_in) return true
      return (
        new Date(value) > new Date(this.form.check_in) ||
        'Check-out must be after check-in'
      )
    },
    close() {
      this.$store.commit('hideModal')
    },
    async save() {
      if (this.$refs.form.validate()) {
        this.saving = true
        try {
          await this.$store.dispatch('reservation/updateReservation', {
            id: this.reservation.id,
            ...this.form,
          })
          this.onSuccess()
          this.$store.commit('hideModal')
        } catch (error) {
          console.error('Error saving reservation override:', error)
          // Could add error notification here
        } finally {
          this.saving = false
        }
      }
    },
  },
}
</script>
